import {
  Button,
  Container,
  EmptyState,
  Heading,
  Link,
  Skeleton,
  Stack,
  Text,
} from "@chakra-ui/react";
import * as React from "react";
import { FaPatreon } from "react-icons/fa6";

import { Creator, getCreator } from "../api/creator";
import {
  Patron,
  createPatron,
  getPatron,
  getPatronEntitlements,
} from "../api/patron";
import { Upload } from "../api/uploads";
import { CreatorShell } from "../components/creator-shell";
import { Footer } from "../components/footer";
import { UploadsGrid } from "../components/uploads-grid";
import { useLoading } from "../hooks/use-loading";
import { useRequiredParams } from "../hooks/use-required-params";
import { useTitle } from "../hooks/use-title";
import { useToaster } from "../hooks/use-toaster";

export function CreatorPage() {
  const { slug } = useRequiredParams(["slug"]);
  const [isLoading, withLoading] = useLoading();
  const [creatorNotFound, setCreatorNotFound] = React.useState(false);
  const [creator, setCreator] = React.useState<Creator | null>(null);
  const [patron, setPatron] = React.useState<Patron | null>(null);
  const [uploads, setUploads] = React.useState<Upload[]>([]);
  useTitle(slug);

  const membershipURL = React.useMemo(() => {
    if (creator === null) return null;
    return `https://www.patreon.com/${creator.slug}/membership`;
  }, [creator]);

  React.useEffect(() => {
    withLoading(async () => {
      try {
        const creator = await getCreator(slug);
        setCreator(creator);
      } catch {
        setCreatorNotFound(true);
      }
      try {
        const patron = await getPatron();
        setPatron(patron);
        const uploads = await getPatronEntitlements(slug);
        setUploads(uploads);
        if (window.location.hash) {
          setTimeout(() => {
            const elementId = window.location.hash.substring(1);
            const element = document.getElementById(elementId);
            if (element) {
              element.scrollIntoView({ behavior: "smooth", block: "center" });
              element.style.animation = "highlight 2s ease-out";
            }
          }, 100);
        }
      } catch {
        setPatron(null);
        setUploads([]);
      }
    });
  }, [slug, withLoading]);

  if (creatorNotFound) {
    return (
      <Container maxW="3xl" py={8}>
        <Stack gap={6}>
          <Stack>
            <Heading>Not Found</Heading>
            <Text>The creator you are looking for does not exist.</Text>
          </Stack>
          <Footer pt={8} />
        </Stack>
      </Container>
    );
  }

  return (
    <Container maxW="3xl" py={8}>
      <Stack gap={6}>
        {isLoading ? (
          <Stack gap={4}>
            <Skeleton height="20px" width="80%" />
            <Skeleton height="40px" width="100%" />
          </Stack>
        ) : (
          creator && (
            <CreatorShell creator={creator}>
              {patron ? (
                uploads.length > 0 ? (
                  <UploadsGrid
                    creator={creator}
                    uploads={uploads}
                    patron={patron}
                  />
                ) : (
                  <EmptyState.Root>
                    <EmptyState.Content>
                      <EmptyState.Indicator>
                        <FaPatreon />
                      </EmptyState.Indicator>
                      <Stack textAlign="center">
                        <EmptyState.Title>No Files Available</EmptyState.Title>
                        <EmptyState.Description>
                          You don&apos;t have access to any of this
                          creator&apos;s files.
                        </EmptyState.Description>
                        {membershipURL && (
                          <Button asChild>
                            <Link href={membershipURL}>
                              Join {creator.vanity} on Patreon
                            </Link>
                          </Button>
                        )}
                      </Stack>
                    </EmptyState.Content>
                  </EmptyState.Root>
                )
              ) : (
                <LoginButton slug={slug} />
              )}
            </CreatorShell>
          )
        )}
        <Footer pt={8} />
      </Stack>
    </Container>
  );
}

interface LoginButtonProps {
  slug: string;
}

const LoginButton = ({ slug }: LoginButtonProps) => {
  const withToaster = useToaster();
  const [isLoading, withLoading] = useLoading();

  const handleLogin = async () => {
    await withLoading(async () => {
      await withToaster(async () => {
        const { "oauth-url": oauthUrl } = await createPatron({ slug });
        window.location.href = oauthUrl;
      });
    });
  };

  return (
    <>
      <Text>Log in with Patreon to access your files.</Text>
      <Button
        colorPalette="gray"
        size="lg"
        onClick={handleLogin}
        loading={isLoading}
      >
        <FaPatreon />
        Log in with Patreon
      </Button>
    </>
  );
};
